import { ContentSwitcher, IconButton, TextInput } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { useGpuCapacityTotal } from "@/components/gpu";
import {
  MAX_SCHEDULE_INTERVAL_MINUTES,
  MIN_SCHEDULE_INTERVAL_MINUTES,
  scheduleWindowIssue,
  type ScheduleRecurrenceMode,
} from "./scheduleDraft";
import {
  CronRecurrenceFields,
  GatedToggle,
  MinFreeGpusField,
  ScheduleDateTimePicker,
} from "./scheduleFields";
import { canFollowBranchTip } from "./submitWizardForm";
import { SubmitField } from "./submitFields";
import type { SubmitWizardController } from "./useSubmitWizardController";
import styles from "./submitWizard.module.css";

// 반복 방식 세그먼트. cron 은 공유 어휘(다섯 자리 식)라 영어로 둔다.
const RECURRENCE_MODE_OPTIONS = [
  { value: "interval", label: "간격" },
  { value: "cron", label: "cron" },
] as const;

/**
 * ③′ Schedule 설정. 이 제출을 언제, 어떤 조건에서 반복할지 정한다.
 *
 * 무엇을 실행할지(데이터셋·구성)는 앞 스텝이 이미 확정했다. 이 스텝은 반복 축(간격|cron)과 기간,
 * 실행 조건 3축(새 Commit 있을 때만 · 여유 GPU · 최신 Release)만 다룬다. 초안 해석과 조건 규칙은
 * `scheduleDraft` 모듈이 소유한다. 컨트롤은 편집 다이얼로그와 같은 `scheduleFields` 를 쓴다.
 */
export function ScheduleStep({ controller }: { controller: SubmitWizardController }): ReactNode {
  const { form, scheduleDraft, updateScheduleDraft, isaacEvaluationEnabled } = controller;
  const totalGpus = useGpuCapacityTotal();
  // 기간 오류는 그 칸에 표시한다. '다음'만 비활성이면 사용자는 어느 칸이 왜 틀렸는지 알 수 없다.
  const windowIssue = scheduleWindowIssue(scheduleDraft);
  // skip 은 추종할 branch 가 있어야 한다. 잠금 판정은 제출 마스킹과 같은 술어를 쓴다.
  const branchFollowAvailable = canFollowBranchTip(form);
  // 릴리스 추종은 활성 eval scenario 가 있어야 한다(없으면 상류가 422). 같은 이유로 잠근다.
  const trackAvailable = isaacEvaluationEnabled;

  return (
    <div className={styles.scheduleStep}>
      <div className={styles.scheduleColumn}>
        <SubmitField label="Schedule 이름" required>
          <TextInput
            value={scheduleDraft.name}
            placeholder="예: 야간 재학습"
            aria-label="Schedule 이름"
            onChange={(event) => updateScheduleDraft({ name: event.target.value })}
          />
        </SubmitField>
        <SubmitField label="설명">
          <TextInput
            value={scheduleDraft.description}
            placeholder="이 반복이 무엇을 위한 것인지 적어 주세요."
            aria-label="설명"
            onChange={(event) => updateScheduleDraft({ description: event.target.value })}
          />
        </SubmitField>

        <SubmitField
          label="반복 방식"
          required
          dividerTop
          hint="간격은 마지막 예정으로부터의 주기, cron은 지정 시간대의 wall-clock 규칙입니다."
        >
          <ContentSwitcher
            label="반복 방식"
            size="sm"
            options={[...RECURRENCE_MODE_OPTIONS]}
            value={scheduleDraft.mode}
            onValueChange={(value) =>
              updateScheduleDraft({ mode: (value as ScheduleRecurrenceMode | null) ?? "interval" })
            }
          />
        </SubmitField>
        {scheduleDraft.mode === "interval" ? (
          <SubmitField
            label="반복 간격 (분)"
            required
            hint={`${MIN_SCHEDULE_INTERVAL_MINUTES}분 이상 ${MAX_SCHEDULE_INTERVAL_MINUTES}분(1년) 이하의 정수입니다.`}
          >
            <TextInput
              value={scheduleDraft.intervalMinutes}
              inputMode="numeric"
              aria-label="반복 간격 (분)"
              onChange={(event) => updateScheduleDraft({ intervalMinutes: event.target.value })}
            />
          </SubmitField>
        ) : (
          <CronRecurrenceFields
            cron={scheduleDraft.cron}
            timezone={scheduleDraft.timezone}
            required
            confirmVerb="제출할 때"
            onChange={updateScheduleDraft}
          />
        )}

        <SubmitField
          label="시작 시각"
          dividerTop
          hint={
            windowIssue?.field === "startAt"
              ? windowIssue.message
              : "비우면 바로 시작합니다. 브라우저 시간대 기준입니다."
          }
        >
          <div className={styles.scheduleDateTimeRow}>
            <ScheduleDateTimePicker
              value={scheduleDraft.startAt}
              ariaLabel="시작 시각"
              onChange={(startAt) => updateScheduleDraft({ startAt })}
            />
            {scheduleDraft.startAt !== "" && (
              <IconButton
                text="지우기"
                aria-label="시작 시각 지우기"
                size="sm"
                variant="ghost"
                onClick={() => updateScheduleDraft({ startAt: "" })}
              />
            )}
          </div>
        </SubmitField>
        <SubmitField
          label="종료 시각"
          hint={
            windowIssue?.field === "endAt"
              ? windowIssue.message
              : "비우면 삭제할 때까지 반복합니다. 브라우저 시간대 기준입니다."
          }
        >
          <div className={styles.scheduleDateTimeRow}>
            <ScheduleDateTimePicker
              value={scheduleDraft.endAt}
              ariaLabel="종료 시각"
              onChange={(endAt) => updateScheduleDraft({ endAt })}
            />
            {scheduleDraft.endAt !== "" && (
              <IconButton
                text="지우기"
                aria-label="종료 시각 지우기"
                size="sm"
                variant="ghost"
                onClick={() => updateScheduleDraft({ endAt: "" })}
              />
            )}
          </div>
        </SubmitField>

        <div className={styles.fieldDividerTop}>
          {/* 사용 가능 여부가 구성 스텝의 branch 선택에 달려 있어서 잠긴 이유를 그 자리에 표시한다. */}
          <GatedToggle
            label="새 Commit이 있을 때만 실행"
            hint={`Branch(${form.branchRef})의 최신 Commit이 직전 실행과 같으면 그 실행을 건너뜁니다.`}
            hintPlacement="below"
            available={branchFollowAvailable}
            lockedReason="구성 스텝에서 Branch를 고르면 켤 수 있습니다."
            checked={scheduleDraft.skipIfUnchanged}
            onChange={(checked) => updateScheduleDraft({ skipIfUnchanged: checked })}
          />
        </div>
        <MinFreeGpusField
          draft={scheduleDraft}
          totalGpus={totalGpus}
          clearsExisting={false}
          placeholder="예: 8"
          onChange={(minFreeGpus) => updateScheduleDraft({ minFreeGpus })}
        />
        {/* Isaac Evaluation 이 켜져 있어야 실행마다 최신 Release Image 를 쓴다. */}
        <GatedToggle
          label="Evaluation Image를 항상 최신 Release로 씁니다"
          hint="실행마다 그 시점의 최신 Release(X.Y.Z) Evaluation Image를 씁니다."
          available={trackAvailable}
          lockedReason="구성 스텝에서 Isaac Evaluation을 켜면 켤 수 있습니다."
          checked={scheduleDraft.trackLatestEvalRelease}
          onChange={(checked) => updateScheduleDraft({ trackLatestEvalRelease: checked })}
        />
      </div>
    </div>
  );
}
